import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Flex, Heading, Text, Button, Select, VStack, HStack, useToast, Card, CardBody,
    Stat, StatLabel, StatNumber, SimpleGrid, Badge, Progress, Divider, Table, Thead, Tbody, Tr, Th, Td, IconButton, Tooltip
} from '@chakra-ui/react';
import { FiPlay, FiUsers, FiDownload, FiClock, FiFileText, FiMessageSquare } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';
import { fetchCourses } from '../services/courseService';
import AuthContext from '../services/AuthContext';
import * as XLSX from 'xlsx';

const AttendanceControl = () => {
    const { user } = useContext(AuthContext);
    const [courses, setCourses] = useState([]);
    const [groupSettings, setGroupSettings] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [activeSessions, setActiveSessions] = useState([]);
    const [recentSessions, setRecentSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingSessionId, setDownloadingSessionId] = useState(null);
    const [sendingSmsSessionId, setSendingSmsSessionId] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const toast = useToast();

    useEffect(() => {
        loadData();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [coursesData, groupsData] = await Promise.all([
                fetchCourses(),
                attendanceService.getWhatsAppGroups()
            ]);
            // If instructor, filter courses
            const myCourses = user.role === 'admin' ? coursesData : coursesData.filter(c => String(c.teacherId) === String(user.id));
            setCourses(myCourses);
            setGroupSettings(groupsData);
            await loadRecentSessions();
        } catch (error) {
            toast({ title: 'خطأ في تحميل البيانات', status: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const loadRecentSessions = async () => {
        try {
            const sessions = await attendanceService.getRecentSessions();
            setRecentSessions(sessions);

            // Collect all active sessions
            const active = sessions.filter(s => s.isActive && new Date(s.endTime) > new Date());
            setActiveSessions(active);
        } catch (error) {
            console.error("Error loading recent sessions", error);
        }
    };

    const handleStart = async () => {
        if (!selectedCourse || !selectedGroup) {
            toast({ title: 'يرجى اختيار المادة والمجموعة', status: 'warning' });
            return;
        }

        // Check if this specific combo is already active
        const isAlreadyActive = activeSessions.some(s => s.matier === selectedCourse && s.group === selectedGroup);
        if (isAlreadyActive) {
            toast({ title: 'هذه الجلسة نشطة بالفعل لهذه المجموعة', status: 'warning' });
            return;
        }

        try {
            const session = await attendanceService.startAttendance({
                matier: selectedCourse,
                group: selectedGroup,
                durationMinutes: 3
            });
            await loadRecentSessions();
            toast({ title: 'تم بدء جلسة Zoom بنجاح!', status: 'success' });
        } catch (error) {
            toast({ title: 'خطأ في بدء الجلسة', description: error.message, status: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadReport = async (sessionId, matier, group) => {
        try {
            setDownloadingSessionId(sessionId);
            const data = await attendanceService.getAbsentees(sessionId);

            if (!data || data.length === 0) {
                toast({ title: 'لا يوجد غائبين لهذه الجلسة', status: 'info' });
                return;
            }

            const excelData = data.map(item => ({
                'Date (التاريخ)': item.date,
                'Time (الوقت)': item.time,
                'Subject (المادة)': item.matier,
                'Group (المجموعة)': item.group,
                'Student Name (اسم الطالب)': item.studentName,
                'Student Email (البريد الإلكتروني)': item.studentEmail,
                'Student Phone (رقم الهاتف)': item.studentPhone,
                'Parent Phone (رقم ولي الأمر)': item.parentPhone
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Absents');

            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `تقرير_غياب_${matier}_${group}_${dateStr}.xlsx`;

            XLSX.writeFile(workbook, filename);
            toast({ title: 'تم تحميل التقرير بنجاح', status: 'success' });
        } catch (error) {
            toast({
                title: 'خطأ في تحميل التقرير',
                description: error.response?.data?.message || 'فشل في تحميل البيانات',
                status: 'error'
            });
        } finally {
            setDownloadingSessionId(null);
        }
    };

    const handleSendSMS = async (sessionId) => {
        try {
            setSendingSmsSessionId(sessionId);
            const response = await attendanceService.sendAbsentSMS(sessionId);

            if (response.success) {
                toast({
                    title: 'تم إرسال الرسائل بنجاح',
                    description: `تمت معالجة ${response.results?.length || 0} طالباً`,
                    status: 'success'
                });
            } else {
                toast({
                    title: 'خطأ في إرسال الرسائل',
                    description: response.message || 'حدث خطأ غير متوقع',
                    status: 'error'
                });
            }
        } catch (error) {
            toast({
                title: 'خطأ في الاتصال',
                description: error.response?.data?.message || 'فشل في إرسال الطلب',
                status: 'error'
            });
        } finally {
            setSendingSmsSessionId(null);
        }
    };

    const formatTimeLeft = (endTime) => {
        const remaining = Math.max(0, Math.floor((new Date(endTime) - currentTime) / 1000));
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    };

    const getProgressValue = (endTime) => {
        const total = 3 * 60; // 3 minutes standard
        const remaining = Math.max(0, Math.floor((new Date(endTime) - currentTime) / 1000));
        return (remaining / total) * 100;
    };

    const availableGroups = groupSettings.find(g => g.matier === selectedCourse)?.groups || [];

    return (
        <Box dir="rtl" fontFamily="Cairo, sans-serif">
            <Heading mb={8} size={{ base: "lg", md: "xl" }} color="blue.700">مركز التحكم في الحضور</Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 8 }}>
                <Card variant="outline" boxShadow="md" borderRadius="xl">
                    <CardBody>
                        <VStack spacing={6} align="stretch">
                            <Heading size="md" color="blue.600">بدء جلسة جديدة</Heading>
                            <Box>
                                <Text mb={2} fontWeight="bold">اختر المادة</Text>
                                <Select
                                    placeholder="اختر المادة"
                                    value={selectedCourse}
                                    onChange={(e) => { setSelectedCourse(e.target.value); setSelectedGroup(''); }}
                                >
                                    {courses.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                </Select>
                            </Box>
                            <Box>
                                <Text mb={2} fontWeight="bold">اختر مجموعة الواتساب</Text>
                                <Select
                                    placeholder="اختر المجموعة"
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                    isDisabled={!selectedCourse}
                                >
                                    {availableGroups.map((g, i) => <option key={i} value={g}>{g}</option>)}
                                </Select>
                            </Box>
                            <Button
                                leftIcon={<FiPlay />}
                                colorScheme="blue"
                                size="lg"
                                onClick={handleStart}
                                isLoading={isLoading}
                                borderRadius="lg"
                                w="100%"
                            >
                                بدء تسجيل حضور جديد
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>

                <VStack spacing={4} align="stretch">
                    <Heading size="md" color="blue.600">الجلسات النشطة ({activeSessions.length})</Heading>
                    {activeSessions.length > 0 ? (
                        activeSessions.map(session => (
                            <Card key={session._id} bg="blue.50" variant="outline" boxShadow="sm" borderRadius="xl">
                                <CardBody p={4}>
                                    <VStack spacing={3} align="stretch">
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="bold" fontSize="md">{session.matier}</Text>
                                                <Text fontSize="sm" color="gray.600">المجموعة: {session.group}</Text>
                                            </Box>
                                            <Stat textAlign="left">
                                                <StatNumber fontSize="xl" color="red.500">
                                                    {formatTimeLeft(session.endTime)}
                                                </StatNumber>
                                            </Stat>
                                        </Flex>
                                        <Progress
                                            value={getProgressValue(session.endTime)}
                                            size="xs"
                                            colorScheme="blue"
                                            borderRadius="full"
                                        />
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))
                    ) : (
                        <Card p={8} bg="gray.50" variant="outline" borderRadius="xl" borderStyle="dashed">
                            <VStack spacing={2}>
                                <FiUsers size={32} color="#CBD5E0" />
                                <Text color="gray.500">لا توجد جلسات نشطة حالياً</Text>
                            </VStack>
                        </Card>
                    )}
                </VStack>
            </SimpleGrid>

            <Divider my={10} />

            <Box mt={4}>
                <Heading size="md" mb={6} color="blue.600">الجلسات الأخيرة والتقارير</Heading>
                <Card variant="outline" borderRadius="xl" overflow="hidden">
                    <Box overflowX="auto">
                        <Table variant="simple" size={{ base: "sm", md: "md" }}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>المادة</Th>
                                    <Th>المجموعة</Th>
                                    <Th display={{ base: 'none', md: 'table-cell' }}>التاريخ</Th>
                                    <Th>الحالة</Th>
                                    <Th>تقرير الغائبين</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {recentSessions.length > 0 ? (
                                    recentSessions.map((session) => (
                                        <Tr key={session._id}>
                                            <Td fontWeight="bold">{session.matier}</Td>
                                            <Td>{session.group}</Td>
                                            <Td display={{ base: 'none', md: 'table-cell' }}>{new Date(session.createdAt).toLocaleString('fr-FR')}</Td>
                                            <Td>
                                                {session.isActive && new Date(session.endTime) > new Date() ? (
                                                    <Badge colorScheme="green">نشط</Badge>
                                                ) : (
                                                    <Badge colorScheme="gray">منتهي</Badge>
                                                )}
                                            </Td>
                                            <Td>
                                                <Button
                                                    onClick={() => handleDownloadReport(session._id, session.matier, session.group)}
                                                    size="sm"
                                                    leftIcon={<FiDownload />}
                                                    colorScheme="green"
                                                    variant="ghost"
                                                    isLoading={downloadingSessionId === session._id}
                                                    isDisabled={session.isActive && new Date(session.endTime) > new Date()}
                                                >
                                                    <Text display={{ base: 'none', sm: 'inline' }}>تحميل Excel</Text>
                                                </Button>
                                                <Button
                                                    onClick={() => handleSendSMS(session._id)}
                                                    size="sm"
                                                    leftIcon={<FiMessageSquare />}
                                                    colorScheme="blue"
                                                    variant="ghost"
                                                    isLoading={sendingSmsSessionId === session._id}
                                                    isDisabled={session.isActive && new Date(session.endTime) > new Date()}
                                                    ml={2}
                                                >
                                                    <Text display={{ base: 'none', sm: 'inline' }}>إرسال SMS</Text>
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                                            لا توجد جلسات سابقة لعرضها
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                </Card>
            </Box>
        </Box>
    );
};

export default AttendanceControl;
